import React, { useEffect, useState, useCallback, useRef } from "react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import { Modal, Spin, Typography } from "antd";
import { ExclamationCircleFilled } from "@ant-design/icons";
import HeaderTab from "../../Components/headerTab/HeaderTab";
import { useAppMessage } from "../../hooks/useAppMessage";
import { useFlightModel } from "../../contexts/FlightModelContext";
import { PlaygroundAPI } from "../../API/PlaygroundAPI";
import { ConfigurationAPI } from "../../API/ConfigurationAPI";
import {
  SCENARIO_BASELINE,
  SCENARIO_FIELDS,
  PARAMETER_FIELD_MAP,
  validateScenarioValues,
  validateSingleField,
  buildPlaygroundBody,
  buildApplyBody,
  mapSimulationRow,
} from "./PlaygroundUtils";
import {
  ScenarioSettingsCard,
  SimulationOverviewGuide,
  RankingComparisonTable,
} from "./components";
import "../../styles/Playground/Playground.css";

const { Text } = Typography;
const { confirm } = Modal;

export default function Playground() {
  const navigate = useNavigate();
  const [messageApi, contextHolder] = useAppMessage();
  const { selectedFlightModel, refreshFlightModels } = useFlightModel();

  // Scenario Parameter State
  const [liveValues, setLiveValues] = useState({ ...SCENARIO_BASELINE });
  const [whatIfValues, setWhatIfValues] = useState({ ...SCENARIO_BASELINE });
  const [errorFields, setErrorFields] = useState({});
  const [isEditing, setIsEditing] = useState(false);

  // Simulation & Table State
  const [pageLoading, setPageLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  const [rankingRows, setRankingRows] = useState([]);
  const [lastSimulatedValues, setLastSimulatedValues] = useState(null);
  const lastSimulatedValuesRef = useRef(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [totalRecords, setTotalRecords] = useState(0);
  const [hasSimulated, setHasSimulated] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [searchText, setSearchText] = useState("");
  const searchTimerRef = useRef(null);
  const comparisonRef = useRef(null);

  // Clear search whenever the comparison grid is closed / reset.
  useEffect(() => {
    if (!hasSimulated) {
      clearTimeout(searchTimerRef.current);
      setSearchInput("");
      setSearchText("");
    }
  }, [hasSimulated]);

  useEffect(() => () => clearTimeout(searchTimerRef.current), []);

  const isModified = useCallback(
    (key) => String(whatIfValues[key]) !== String(liveValues[key]),
    [whatIfValues, liveValues]
  );

  const anyModified = SCENARIO_FIELDS.some((field) => isModified(field.key));

  const isStale =
    hasSimulated &&
    Boolean(lastSimulatedValues) &&
    SCENARIO_FIELDS.some(
      (field) =>
        String(whatIfValues[field.key]) !==
        String(lastSimulatedValues[field.key])
    );

  const messageToast = useCallback(
    (type, content) => {
      messageApi.open({ type, content, duration: 6 });
    },
    [messageApi]
  );

  const resolveFlightModel = useCallback(async () => {
    let flightModelToUse = selectedFlightModel;

    if (!flightModelToUse) {
      const refreshed = await refreshFlightModels();
      flightModelToUse = refreshed?.selectedFlightModel || null;
    }

    if (!flightModelToUse) {
      messageToast("error", "No flight model selected.");
    }

    return flightModelToUse;
  }, [selectedFlightModel, refreshFlightModels, messageToast]);

  const loadLiveConfig = useCallback(async () => {
    let flightModelToUse = selectedFlightModel;

    if (!flightModelToUse) {
      const refreshed = await refreshFlightModels();
      flightModelToUse = refreshed?.selectedFlightModel || null;
    }

    if (!flightModelToUse) {
      messageToast("error", "No flight model selected.");
      return;
    }

    const queryParams = { params: { flight_model: flightModelToUse } };

    setPageLoading(true);
    try {
      const res = await ConfigurationAPI.getCofigData(queryParams);
      if (res.data?.status === true) {
        const live = {
          fleetCount: res.data?.data?.avg_fleet_size,
          flightHours: res.data?.data?.per_dy_fl_hr,
          flightCycle: res.data?.data?.per_dy_fl_cy,
          laborRate: res.data?.data?.labour_rate,
          aircraftLifTime: res.data?.data?.avg_life_cy,
        };
        setLiveValues(live);
        setWhatIfValues(live);
        setErrorFields({});
        setRankingRows([]);
        setLastSimulatedValues(null);
        lastSimulatedValuesRef.current = null;
        setPage(1);
        setTotalRecords(0);
        setHasSimulated(false);
      } else {
        messageToast("error", res.data?.message);
      }
    } catch {
      messageToast("error", "Internal Server Error");
    } finally {
      setPageLoading(false);
    }
  }, [selectedFlightModel, refreshFlightModels, messageToast]);

  useEffect(() => {
    loadLiveConfig();
  }, [loadLiveConfig]);

  const handleValueChange = useCallback((key, value) => {
    setWhatIfValues((prev) => ({ ...prev, [key]: value }));
    const error = validateSingleField(key, value);
    setErrorFields((prev) => ({ ...prev, [key]: error }));
  }, []);

  const runSimulation = useCallback(
    async (
      values = whatIfValues,
      pageNum = page,
      pageSizeNum = pageSize,
      search = searchText
    ) => {
      const flightModelToUse = await resolveFlightModel();
      if (!flightModelToUse) return;

      const bodyData = buildPlaygroundBody(
        flightModelToUse,
        values,
        pageNum,
        pageSizeNum,
        search
      );

      setTableLoading(true);
      try {
        const res = await PlaygroundAPI.runSimulation(bodyData);
        const resData = res?.status !== undefined ? res : res?.data || {};

        if (resData?.status === true) {
          const rawRows =
            resData?.rows ||
            resData?.preview ||
            (Array.isArray(resData?.data) ? resData.data : []) ||
            [];
          setRankingRows(rawRows.map(mapSimulationRow));
          setLastSimulatedValues({ ...values });
          lastSimulatedValuesRef.current = { ...values };
          setPage(resData?.page || pageNum);
          setPageSize(resData?.page_size || pageSizeNum);

          const calculatedTotal =
            resData?.summary?.total_specs ??
            resData?.total_records ??
            resData?.total_specs ??
            resData?.total ??
            (resData?.total_pages && resData?.page_size
              ? resData.total_pages * resData.page_size
              : rawRows.length);
          setTotalRecords(calculatedTotal);
          setHasSimulated(true);

          setTimeout(() => {
            comparisonRef.current?.scrollIntoView({
              behavior: "smooth",
              block: "nearest",
            });
          }, 150);
        } else {
          messageToast("error", resData?.message || "Simulation failed");
        }
      } catch {
        messageToast("error", "Internal Server Error");
      } finally {
        setTableLoading(false);
      }
    },
    [whatIfValues, page, pageSize, searchText, resolveFlightModel, messageToast]
  );

  const handleToggleEdit = useCallback(() => {
    if (isEditing) {
      setWhatIfValues({ ...liveValues });
      setErrorFields({});
      setIsEditing(false);
    } else {
      setIsEditing(true);
    }
  }, [isEditing, liveValues]);

  const handleReset = useCallback(() => {
    setWhatIfValues({ ...liveValues });
    setErrorFields({});
    setRankingRows([]);
    setLastSimulatedValues(null);
    lastSimulatedValuesRef.current = null;
    setPage(1);
    setTotalRecords(0);
    setHasSimulated(false);
  }, [liveValues]);

  const handleRunSimulation = useCallback(() => {
    const { isValid, errors } = validateScenarioValues(whatIfValues);
    if (!isValid) {
      setErrorFields(errors);
      return;
    }
    setErrorFields({});
    setPage(1);
    clearTimeout(searchTimerRef.current);
    setSearchInput("");
    setSearchText("");
    runSimulation(whatIfValues, 1, pageSize, "");
  }, [whatIfValues, pageSize, runSimulation]);

  const handleSearchChange = useCallback(
    (e) => {
      const value = e.target.value;
      setSearchInput(value);
      clearTimeout(searchTimerRef.current);
      searchTimerRef.current = setTimeout(() => {
        const trimmed = value.trim();
        setSearchText(trimmed);
        setPage(1);
        runSimulation(
          lastSimulatedValuesRef.current || whatIfValues,
          1,
          pageSize,
          trimmed
        );
      }, 400);
    },
    [whatIfValues, pageSize, runSimulation]
  );

  const handleTableChange = useCallback(
    (pagination) => {
      const { current, pageSize: newSize } = pagination;
      setPage(current);
      setPageSize(newSize);
      if (hasSimulated) {
        runSimulation(
          lastSimulatedValuesRef.current || whatIfValues,
          current,
          newSize,
          searchText
        );
      }
    },
    [hasSimulated, whatIfValues, searchText, runSimulation]
  );

  const handleDiscard = useCallback(() => {
    if (!anyModified) {
      navigate("/ranking");
      return;
    }
    confirm({
      title: "Discard changes and exit?",
      icon: <ExclamationCircleFilled />,
      content:
        "Your hypothetical values have not been applied. Exiting now will discard them and the live configuration will remain unchanged.",
      okText: "Discard & Exit",
      okType: "danger",
      cancelText: "Stay",
      onOk() {
        navigate("/ranking");
      },
    });
  }, [anyModified, navigate]);

  const applyChanges = useCallback(async () => {
    const flightModelToUse = await resolveFlightModel();
    if (!flightModelToUse) return;

    const bodyData = buildApplyBody(flightModelToUse, whatIfValues);

    setPageLoading(true);
    try {
      const res = await PlaygroundAPI.applyChanges(bodyData);

      if (res?.status >= 400 || res instanceof Error) {
        let errMsg =
          typeof res?.data === "string"
            ? res.data
            : res?.data?.message || res?.data?.detail || "Apply Changes failed";
        if (res?.status === 500) {
          errMsg =
            "Server calculation error: Please ensure operational parameters (e.g. Flight Cycles <= 18/day) are within realistic operating limits.";
        }
        messageToast("error", errMsg);
        return;
      }

      const responseData =
        res?.data?.status !== undefined ? res.data : (res?.data || res);
      const isSuccess =
        responseData?.status === true || res?.status === true;

      if (isSuccess) {
        const updatedLive = { ...liveValues };
        const configList = responseData?.config || res?.config || [];
        configList.forEach((item) => {
          const key = PARAMETER_FIELD_MAP[item.parameter];
          if (key) updatedLive[key] = item.modified_value;
        });

        setLiveValues(updatedLive);
        setWhatIfValues(updatedLive);
        setRankingRows([]);
        setLastSimulatedValues(null);
        lastSimulatedValuesRef.current = null;
        setHasSimulated(false);
        setIsEditing(false);

        Modal.success({
          title: "Changes Applied",
          content: (
            <>
              <Text>
                {responseData?.message ||
                  res?.data?.message ||
                  "Changes applied. The Ranking Grid has been recalculated and updated for all users."}
              </Text>
              {(responseData?.warning || res?.data?.warning) && (
                <Text
                  type="warning"
                  style={{ display: "block", marginTop: 8 }}
                >
                  {responseData?.warning || res?.data?.warning}
                </Text>
              )}
              {responseData?.impact && (
                <Text
                  type="secondary"
                  style={{ display: "block", marginTop: 8, fontSize: "12px" }}
                >
                  Live rankings changed for {responseData.impact.specs_rank_changed} of{" "}
                  {responseData.impact.total_specs} specification(s).
                </Text>
              )}
            </>
          ),
          onOk: () => navigate("/ranking"),
        });
      } else {
        messageToast(
          "error",
          responseData?.message || res?.data?.message || res?.message || "Apply Changes failed"
        );
      }
    } catch (err) {
      const errMsg =
        err?.response?.data?.message ||
        err?.response?.data?.detail ||
        err?.message ||
        "Internal Server Error";
      messageToast("error", errMsg);
    } finally {
      setPageLoading(false);
    }
  }, [resolveFlightModel, whatIfValues, liveValues, messageToast, navigate]);

  const handleApplyChanges = useCallback(() => {
    const { isValid, errors } = validateScenarioValues(whatIfValues);
    if (!isValid) {
      setErrorFields(errors);
      return;
    }

    confirm({
      title: "Apply these changes to the live configuration?",
      icon: <ExclamationCircleFilled />,
      content:
        "The proposed values will replace the live configuration and the ranking will be recalculated using the updated values.",
      okText: "Apply Changes",
      cancelText: "Cancel",
      onOk: applyChanges,
    });
  }, [whatIfValues, applyChanges]);

  return (
    <div className="container_div">
      <Helmet>
        <title>SpecOptimizer | Proposed Playground</title>
      </Helmet>
      <Spin spinning={pageLoading || tableLoading}>
        <HeaderTab currrentTab="settings" />

        {/* Top Split: Scenario Settings + Live Overview & Simulation Guide */}
        <div className="playground_top_split">
          <ScenarioSettingsCard
            liveValues={liveValues}
            whatIfValues={whatIfValues}
            isEditing={isEditing}
            errorFields={errorFields}
            onToggleEdit={handleToggleEdit}
            onValueChange={handleValueChange}
            onReset={handleReset}
            onRunSimulation={handleRunSimulation}
            isModified={isModified}
          />

          <SimulationOverviewGuide
            selectedFlightModel={selectedFlightModel}
          />
        </div>

        {/* Ranking Comparison (Simulation Result) - Only visible after running simulation */}
        {hasSimulated && (
          <RankingComparisonTable
            ref={comparisonRef}
            rankingRows={rankingRows}
            page={page}
            pageSize={pageSize}
            totalRecords={totalRecords}
            searchInput={searchInput}
            isSearching={Boolean(searchText)}
            isStale={isStale}
            onSearchChange={handleSearchChange}
            onTableChange={handleTableChange}
            onDiscard={handleDiscard}
            onApplyChanges={handleApplyChanges}
          />
        )}
      </Spin>
      {contextHolder}
    </div>
  );
}
